/**
 * Bestellnummer-Pruefung und Batch-Aufruf fuer den Anlage-Assistenten (Modul 38 / A3).
 *
 * Die TYPEN kommen seit dem Vertrags-Commit 680f5edb aus `@/api/purchase`
 * (Abschnitt „VERTRAG B2/B4/B6", eingefroren) — die vorher hier gefuehrten lokalen Nachbauten
 * sind entfernt. Geblieben sind zwei Dinge, die zur OBERFLAECHE gehoeren und nicht zum
 * API-Vertrag: die Format-Pruefung der nacherfassten Nummer und die Erzeugung der
 * `client_batch_id`.
 *
 * `bestellungenAnlegen` ist die EINE Stelle, die den Endpunkt kennt. Sobald P10 dafuer einen
 * Hook liefert, wird genau diese Funktion umgehaengt.
 */
import { api, apiUpload } from "@/api/client";
import type { BatchCreateOrdersInput, BatchCreateOrdersResponse, VorschlagResponse } from "@/api/purchase";

/**
 * Legt alle Lieferantengruppen in EINEM Aufruf an.
 *
 * Ein Aufruf und nicht n Aufrufe, weil ein Teilerfolg ueber Gruppen hinweg nicht reparierbar
 * waere: Gruppe 1 angelegt, Gruppe 2 gescheitert, Wiederholung erzeugt Dubletten
 * (Codex-Befund 15). Der Schutz dagegen ist die `client_batch_id` — dieselbe Id noch einmal
 * gesendet legt NICHTS neu an, der Server antwortet mit derselben Nutzlast und
 * `wiederholung: true`.
 */
export async function bestellungenAnlegen(
  anfrage: BatchCreateOrdersInput,
): Promise<BatchCreateOrdersResponse> {
  return api.post<BatchCreateOrdersResponse>("/purchase/orders/batch/", anfrage);
}

/**
 * Bestellnummer-Pruefung fuer die Nacherfassung.
 *
 * Kurzform ist `<seq><MM><JJJJ><seq>` — die laufende Nummer steht VORNE und HINTEN, dazwischen
 * Monat und Jahr (Beispiel `161082026161`: seq 161, August 2026).
 *
 * Zwei Regeln aus dem Design v2 §7.1 (Zeilen zu Codex 6 und 7), beide bewusst:
 *
 * 1. **Kein Abgleich mit dem Bestelldatum.** Der User kennt seine Nummerierung; eine
 *    nacherfasste `156112025156` bleibt gueltig, auch wenn er als Bestelldatum etwas anderes
 *    waehlt. Wer hier „hilfreich" pruefen wollte, wuerde genau die Nacherfassung verhindern,
 *    fuer die das Feld da ist.
 * 2. **Nur Kurzform.** Die Vollform darf gelesen (Dokumente, Suche), aber nicht mehr
 *    geschrieben werden — sonst stuenden zwei Schreibweisen derselben Bestellung in der
 *    Datenbank.
 */
export interface NummerPruefung {
  gueltig: boolean;
  seq?: number;
  /** Klartext fuer die Oberflaeche — nie ein Code, den niemand lesen kann. */
  meldung?: string;
}

export function pruefeBestellnummer(eingabe: string): NummerPruefung {
  const text = eingabe.trim();
  if (text === "") return { gueltig: false, meldung: "Bitte eine Bestellnummer eingeben." };
  if (!/^\d+$/.test(text)) return { gueltig: false, meldung: "Nur Ziffern — Beispiel: 161082026161" };
  // Der mittlere Block ist immer 6 Ziffern (MMJJJJ); der Rest teilt sich gleichmaessig auf
  // Anfang und Ende auf.
  const rest = text.length - 6;
  if (rest < 2 || rest % 2 !== 0) {
    return { gueltig: false, meldung: "Format: <lfd. Nr><MM><JJJJ><lfd. Nr> — Beispiel: 161082026161" };
  }
  const laenge = rest / 2;
  const vorne = text.slice(0, laenge);
  const hinten = text.slice(-laenge);
  const mitte = text.slice(laenge, laenge + 6);
  if (vorne !== hinten) {
    return { gueltig: false, meldung: "Vorne und hinten muss dieselbe laufende Nummer stehen." };
  }
  const monat = Number(mitte.slice(0, 2));
  const jahr = Number(mitte.slice(2));
  if (monat < 1 || monat > 12) return { gueltig: false, meldung: `Monat ${mitte.slice(0, 2)} gibt es nicht.` };
  if (jahr < 2000 || jahr > 2999) return { gueltig: false, meldung: `Jahr ${jahr} sieht nicht richtig aus.` };
  const seq = Number(vorne);
  if (!Number.isSafeInteger(seq) || seq <= 0) return { gueltig: false, meldung: "Laufende Nummer muss > 0 sein." };
  // Fuehrende Null in der laufenden Nummer waere eine zweite Schreibweise derselben Nummer.
  if (vorne.length > 1 && vorne.startsWith("0")) {
    return { gueltig: false, meldung: "Laufende Nummer ohne führende Null angeben." };
  }
  return { gueltig: true, seq };
}

/**
 * Erzeugt eine client_batch_id.
 *
 * `crypto.randomUUID` ist in allen Zielbrowsern da; der Rueckfall existiert nur, damit die
 * Oberflaeche in aelteren Testumgebungen nicht stehen bleibt — eine Bestellung ohne Id waere
 * eine ohne Doppelklick-Schutz.
 */
export function neueBatchId(): string {
  const c = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `batch-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * B6 — Vorschlag aus einer Datei (CSV/XLSX, spaeter PDF).
 *
 * Die Antwort wird NICHT gespeichert: der Server liest die Datei, schlaegt Spalten und
 * Artikel vor und gibt alles zurueck. Erst das Bestaetigen im Assistenten legt etwas an —
 * ueber denselben Batch-Weg wie die Handanlage. Damit gibt es keinen zweiten Schreibpfad
 * und keine halb angelegte Bestellung aus einer Vorschau.
 *
 * Zweite Stelle, die beim Vertrags-Merge umgehaengt wurde: sie ruft `apiUpload`, weil der
 * Inhalt eine Datei ist und kein JSON.
 */
export async function vorschlagAusDatei(
  datei: File,
  lieferantId?: number | null,
): Promise<VorschlagResponse> {
  const formular = new FormData();
  // Modul 43: der gewaehlte Lieferant MUSS vor der Datei im Formular stehen.
  // `@fastify/multipart` liefert dem Server in `data.fields` nur die Felder, die
  // VOR dem Datei-Teil kamen — danach angehaengte sieht die Route nicht mehr.
  // Der Server prueft die Id selbst gegen die Lieferantentabelle; hier wird nichts
  // vertraut, nur mitgeschickt.
  if (typeof lieferantId === "number") {
    formular.append("supplier_id", String(lieferantId));
  }
  formular.append("file", datei);
  return apiUpload<VorschlagResponse>("/purchase/orders/vorschlag/", formular);
}
